/**
 * KiwiSaver comparison helper functions.
 * Pure functions for building comparison cards, calculating fee impact,
 * and formatting return values.
 */

export function calculateFeeImpact(balance, years, feePercent) {
  if (!balance || !years || feePercent == null) return null
  const feeRate = feePercent / 100
  let totalFees = 0
  let currentBalance = balance

  for (let i = 0; i < years; i++) {
    const yearFee = currentBalance * feeRate
    totalFees += yearFee
    currentBalance -= yearFee
  }

  return {
    totalFees: Math.round(totalFees * 100) / 100,
    remainingBalance: Math.round(currentBalance * 100) / 100
  }
}

export function formatReturn(value) {
  if (value === "" || value == null) return { text: "—", className: "text-stone-400" }
  const num = parseFloat(value)
  if (isNaN(num)) return { text: "—", className: "text-stone-400" }

  const sign = num >= 0 ? "+" : ""
  return {
    text: `${sign}${num.toFixed(2)}%`,
    className: num >= 0 ? "text-emerald-600" : "text-red-600"
  }
}

export function formatMoney(value) {
  if (value == null || isNaN(value)) return "—"
  return new Intl.NumberFormat("en-NZ", {
    style: "currency",
    currency: "NZD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

export function formatFundSize(value) {
  if (!value || value === "") return "—"
  const num = parseFloat(value)
  if (isNaN(num)) return "—"

  if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(1)}B`
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(0)}M`
  return formatMoney(num)
}

export function buildComparisonCard(row) {
  const data = row.dataset
  const ret1 = formatReturn(data.return_1yr || data.return1yr)
  const ret3 = formatReturn(data.return_3yr || data.return3yr)
  const ret5 = formatReturn(data.return_5yr || data.return5yr)
  const fees = data.fees !== "" ? `${parseFloat(data.fees).toFixed(2)}%` : "—"
  const ethical = data.ethical === "true" ? "Yes" : "No"
  const size = formatFundSize(data.fundSize)
  const members = data.members ? parseInt(data.members, 10).toLocaleString() : "—"

  return `
    <div class="moneysmart-card p-4" data-compare-card-id="${data.fundId}">
      <h4 class="font-semibold text-stone-900 text-sm mb-1">${data.providerName}</h4>
      <p class="text-stone-600 text-sm mb-3">${data.fundName}</p>
      <dl class="space-y-2 text-sm">
        <div class="flex justify-between">
          <dt class="text-stone-500">Type</dt>
          <dd class="font-medium">${(data.fundType || "").replace(/^\w/, c => c.toUpperCase())}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-stone-500">1yr Return</dt>
          <dd class="font-medium ${ret1.className}">${ret1.text}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-stone-500">3yr Return</dt>
          <dd class="font-medium ${ret3.className}">${ret3.text}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-stone-500">5yr Return</dt>
          <dd class="font-medium ${ret5.className}">${ret5.text}</dd>
        </div>
        <div class="flex justify-between border-t border-stone-100 pt-2">
          <dt class="text-stone-500">Annual Fees</dt>
          <dd class="font-bold">${fees}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-stone-500">Fund Size</dt>
          <dd class="font-medium">${size}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-stone-500">Members</dt>
          <dd class="font-medium">${members}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-stone-500">Ethical</dt>
          <dd class="font-medium">${ethical}</dd>
        </div>
      </dl>
    </div>
  `
}

export function buildFeeResultRow(providerName, fundName, feePercent, impact) {
  return `
    <div class="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
      <div>
        <span class="font-medium text-stone-900 text-sm">${providerName}</span>
        <span class="text-stone-500 text-sm"> — ${fundName}</span>
        <span class="text-stone-400 text-xs ml-1">(${feePercent}%)</span>
      </div>
      <div class="text-right">
        <span class="font-bold text-red-600 text-sm">${formatMoney(impact.totalFees)}</span>
        <span class="text-stone-400 text-xs ml-1">in fees</span>
      </div>
    </div>
  `
}
